import { DataList, Editable, IconButton, Text } from '@chakra-ui/react';
import { User } from '@/api/User';
import { userService } from '@/service/UserService';
import { LuCheck, LuPencilLine, LuX } from 'react-icons/lu';

type UserProfileSectionProps = {
    user: User | null;
    setIsLoading: (loading: boolean) => void;
    onUserChange: (user: User) => void;
    canEdit?: boolean;
};

const UserProfileSection = ({ user, setIsLoading, onUserChange, canEdit = true }: UserProfileSectionProps) => {
    const handleFirstNameCommit = async (firstName: string) => {
        if (!user) return;
        if (firstName === user.firstName) return;

        setIsLoading(true);
        try {
            await userService.update(user.id, firstName, user.lastName);
            onUserChange({ ...user, firstName });
        } finally {
            setIsLoading(false);
        }
    };

    const handleLastNameCommit = async (lastName: string) => {
        if (!user) return;
        if (lastName === user.lastName) return;

        setIsLoading(true);
        try {
            await userService.update(user.id, user.firstName, lastName);
            onUserChange({ ...user, lastName });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <DataList.Root orientation={{ base: 'vertical', sm: 'horizontal' }} gap={{ base: '4', sm: '0' }}>
            <DataList.Item key='firstname' flexDirection={{ sm: 'row' }}>
                <DataList.ItemLabel>Vorname</DataList.ItemLabel>
                <DataList.ItemValue>
                    {!canEdit ? (
                        <Text>{user?.firstName ?? '-'}</Text>
                    ) : (
                        <Editable.Root
                        key={`firstname-${user?.id ?? 'none'}-${user?.firstName ?? ''}`}
                        defaultValue={user?.firstName}
                        onValueCommit={(details) => { void handleFirstNameCommit(details.value); }}
                        display='flex'
                        alignItems='center'
                        justifyContent='space-between'
                        gap='0.5rem'
                        w='100%'
                    >
                        <Editable.Area flex='1'>
                            <Editable.Preview />
                            <Editable.Input />
                        </Editable.Area>
                        <Editable.Control>
                            <Editable.EditTrigger asChild>
                                <IconButton aria-label="Bearbeiten" colorPalette="blue" variant="ghost" size="xs">
                                    <LuPencilLine />
                                </IconButton>
                            </Editable.EditTrigger>
                            <Editable.CancelTrigger asChild>
                                <IconButton aria-label="Abbrechen" colorPalette="gray" variant="outline" size="xs">
                                    <LuX />
                                </IconButton>
                            </Editable.CancelTrigger>
                            <Editable.SubmitTrigger asChild>
                                <IconButton aria-label="Speichern" colorPalette="blue" variant="outline" size="xs">
                                    <LuCheck />
                                </IconButton>
                            </Editable.SubmitTrigger>
                        </Editable.Control>
                    </Editable.Root>
                    )}
                </DataList.ItemValue>
            </DataList.Item>
            <DataList.Item key='lastname' flexDirection={{ sm: 'row' }}>
                <DataList.ItemLabel>Nachname</DataList.ItemLabel>
                <DataList.ItemValue>
                    {!canEdit ? (
                        <Text>{user?.lastName ?? '-'}</Text>
                    ) : (
                        <Editable.Root
                        key={`lastname-${user?.id ?? 'none'}-${user?.lastName ?? ''}`}
                        defaultValue={user?.lastName}
                        onValueCommit={(details) => { void handleLastNameCommit(details.value); }}
                        display='flex'
                        alignItems='center'
                        justifyContent='space-between'
                        gap='0.5rem'
                        w='100%'
                    >
                        <Editable.Area flex='1'>
                            <Editable.Preview />
                            <Editable.Input />
                        </Editable.Area>
                        <Editable.Control>
                            <Editable.EditTrigger asChild>
                                <IconButton aria-label="Bearbeiten" colorPalette="blue" variant="ghost" size="xs">
                                    <LuPencilLine />
                                </IconButton>
                            </Editable.EditTrigger>
                            <Editable.CancelTrigger asChild>
                                <IconButton aria-label="Abbrechen" colorPalette="gray" variant="outline" size="xs">
                                    <LuX />
                                </IconButton>
                            </Editable.CancelTrigger>
                            <Editable.SubmitTrigger asChild>
                                <IconButton aria-label="Speichern" colorPalette="blue" variant="outline" size="xs">
                                    <LuCheck />
                                </IconButton>
                            </Editable.SubmitTrigger>
                        </Editable.Control>
                    </Editable.Root>
                    )}
                </DataList.ItemValue>
            </DataList.Item>
        </DataList.Root>
    );
};

export default UserProfileSection;
